/**
 * Mode Selector Component
 * Allows users to switch between Design, Execution, and Evaluation modes
 *
 * Features:
 * - Three mode buttons with visual indicators
 * - Permission-based access control
 * - Active state styling
 * - Prevents mode switching during active execution
 * - Toast notifications for permission errors
 * - Keyboard shortcut hints
 * - Glass morphism styling
 */

import React, { useCallback } from 'react';
import { useDesignerModeStore } from '@flow-studio/store'; // import { designerModeStore';
import { useAuthStore } from '@flow-studio/store'; // import { authStore';
import { useUIStore } from '@flow-studio/store'; // import { uiStore';
import type { UIDesignerMode } from '@flow-studio/types';
import './ModeSelector.css';
import { BarChartIcon } from '@bizfirst/common-react';

// ============================================================================
// MODE CONFIGURATION
// ============================================================================

interface ModeButtonConfig {
  mode: UIDesignerMode;
  label: string;
  icon: React.ReactNode;
  shortcut: string;
  permission: 'canEditWorkflows' | 'canExecuteWorkflows' | 'canViewExecutionHistory';
  description: string;
}

const DesignIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9"/>
    <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4 12.5-12.5z"/>
  </svg>
);

const MODE_BUTTONS: ModeButtonConfig[] = [
  {
    mode: 'design',
    label: 'Design',
    icon: <DesignIcon />,
    shortcut: 'Ctrl+1',
    permission: 'canEditWorkflows',
    description: 'Edit and configure workflow',
  },
  {
    mode: 'evaluation',
    label: 'Evaluate',
    icon: <BarChartIcon size={14} />,
    shortcut: 'Ctrl+3',
    permission: 'canViewExecutionHistory',
    description: 'Review execution history and results',
  },
];

// ============================================================================
// COMPONENT
// ============================================================================

export const ModeSelector: React.FC = () => {
  // Get state from stores
  const mode = useDesignerModeStore((s: any) => s.mode);
  const setMode = useDesignerModeStore((s: any) => s.setMode);
  const executionState = useDesignerModeStore((s: any) => s.executionState);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const { showToast, theme } = useUIStore();

  // ========================================================================
  // EVENT HANDLERS
  // ========================================================================

  /**
   * Handle mode button click
   * Checks permissions and execution state before switching
   */
  const handleModeSwitch = useCallback(
    (newMode: UIDesignerMode, config: ModeButtonConfig) => {
      // Check if user has permission for this mode
      if (!hasPermission(config.permission)) {
        showToast(
          'error',
          `You don't have permission to access ${newMode} mode`
        );
        return;
      }

      // Prevent switching modes if execution is active
      if (
        (executionState === 'running' || executionState === 'paused') &&
        newMode !== 'execution'
      ) {
        showToast(
          'warning',
          'Cannot switch modes while execution is active. Stop or complete the execution first.'
        );
        return;
      }

      // Allow mode switch
      setMode(newMode);
    },
    [executionState, hasPermission, setMode, showToast]
  );

  // ========================================================================
  // RENDER
  // ========================================================================

  return (
    <div className={`mode-selector${theme === 'light' ? ' light' : ''}`}>
      {MODE_BUTTONS.map((config) => {
        const isActive = mode === config.mode;

        return (
          <button
            key={config.mode}
            className={`mode-btn ${isActive ? 'active' : ''}`}
            onClick={() => handleModeSwitch(config.mode, config)}
            title={`${config.description} (${config.shortcut})`}
            aria-label={`${config.label} mode`}
            aria-pressed={isActive}
          >
            <span className="mode-icon">{config.icon}</span>
            <span className="mode-label">{config.label}</span>
          </button>
        );
      })}
    </div>
  );
};

// ============================================================================
// EXPORTS
// ============================================================================

export type { ModeButtonConfig };
